/**
 * Security Threat Intelligence Service
 * Provides advanced threat detection and intelligence capabilities
 * Phase 5E: Security Monitoring & Incident Response
 */

import Logger from './logger';

export interface ThreatIndicator {
  id: string;
  type: 'ip_address' | 'domain' | 'url' | 'file_hash' | 'email' | 'user_agent' | 'behavior_pattern';
  value: string;
  confidence: number; // 0-100
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: 'internal' | 'external' | 'crowdsourced' | 'automated';
  firstSeen: Date;
  lastSeen: Date;
  frequency: number;
  context: ThreatContext;
  tags: string[];
  verified: boolean;
  falsePositive: boolean;
}

export interface ThreatContext {
  attackVector: string;
  targetSystem: string;
  attackType: string;
  geographicLocation?: {
    country: string;
    region: string;
    city: string;
    coordinates: { lat: number; lng: number };
  };
  timePattern: {
    hour: number;
    dayOfWeek: number;
    month: number;
  };
  userAgent?: string;
  referrer?: string;
  sessionId?: string;
  userId?: string;
}

export interface ThreatPattern {
  id: string;
  name: string;
  description: string;
  patternType: 'signature' | 'behavioral' | 'anomaly' | 'correlation';
  indicators: ThreatIndicator[];
  rules: ThreatRule[];
  confidence: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'malware' | 'phishing' | 'ddos' | 'insider_threat' | 'data_exfiltration' | 'lateral_movement' | 'persistence';
  created: Date;
  lastUpdated: Date;
  active: boolean;
  falsePositiveRate: number;
  detectionCount: number;
}

export interface ThreatRule {
  id: string;
  name: string;
  condition: string;
  operator: 'equals' | 'contains' | 'regex' | 'range' | 'exists' | 'not_exists';
  value: any;
  weight: number;
  required: boolean;
}

export interface ThreatIntelligence {
  id: string;
  source: string;
  title: string;
  description: string;
  threatType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  indicators: ThreatIndicator[];
  affectedSystems: string[];
  mitigation: string[];
  references: string[];
  published: Date;
  expires?: Date;
  tags: string[];
  verified: boolean;
}

export interface ThreatAnalysis {
  threatId: string;
  analysisType: 'real_time' | 'historical' | 'predictive' | 'correlation';
  timestamp: Date;
  findings: ThreatFinding[];
  riskScore: number;
  recommendations: string[];
  confidence: number;
  falsePositiveProbability: number;
  relatedThreats: string[];
  attackTimeline?: AttackTimelineEvent[];
}

export interface ThreatFinding {
  id: string;
  type: 'indicator_match' | 'behavior_anomaly' | 'pattern_match' | 'correlation_match';
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  evidence: any;
  source: string;
  timestamp: Date;
}

export interface AttackTimelineEvent {
  timestamp: Date;
  event: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  indicators: string[];
}

export interface ThreatIntelligenceConfig {
  enableRealTimeAnalysis: boolean;
  enableHistoricalAnalysis: boolean;
  enablePredictiveAnalysis: boolean;
  enableCorrelationAnalysis: boolean;
  enableExternalFeeds: boolean;
  enableBehavioralAnalysis: boolean;
  analysisInterval: number; // milliseconds
  threatRetentionDays: number;
  confidenceThreshold: number;
  falsePositiveThreshold: number;
  externalFeeds: ExternalFeed[];
  behavioralBaselines: BehavioralBaseline[];
}

export interface ExternalFeed {
  id: string;
  name: string;
  url: string;
  type: 'ioc' | 'malware' | 'phishing' | 'vulnerability' | 'threat_actor';
  format: 'json' | 'csv' | 'xml' | 'stix' | 'misp';
  updateInterval: number; // minutes
  enabled: boolean;
  lastUpdate?: Date;
  apiKey?: string;
}

export interface BehavioralBaseline {
  id: string;
  name: string;
  system: string;
  metric: string;
  baseline: {
    mean: number;
    stdDev: number;
    min: number;
    max: number;
    percentile95: number;
    percentile99: number;
  };
  windowSize: number; // days
  lastUpdated: Date;
}

export interface ThreatIntelligenceStats {
  totalThreats: number;
  threatsByType: Record<string, number>;
  threatsBySeverity: Record<string, number>;
  threatsBySource: Record<string, number>;
  averageConfidence: number;
  falsePositiveRate: number;
  detectionRate: number;
  responseTime: number;
  activePatterns: number;
  recentThreats: ThreatIntelligence[];
  topAttackVectors: Array<{ vector: string; count: number }>;
  geographicDistribution: Array<{ country: string; count: number }>;
}

class SecurityThreatIntelligenceService {
  private static instance: SecurityThreatIntelligenceService;
  private threats: Map<string, ThreatIntelligence> = new Map();
  private indicators: Map<string, ThreatIndicator> = new Map();
  private patterns: Map<string, ThreatPattern> = new Map();
  private analyses: Map<string, ThreatAnalysis> = new Map();
  private config: Required<ThreatIntelligenceConfig>;
  private stats: ThreatIntelligenceStats;
  private analysisInterval: NodeJS.Timeout | null = null;

  constructor(config: Partial<ThreatIntelligenceConfig> = {}) {
    this.config = {
      enableRealTimeAnalysis: config.enableRealTimeAnalysis ?? true,
      enableHistoricalAnalysis: config.enableHistoricalAnalysis ?? true,
      enablePredictiveAnalysis: config.enablePredictiveAnalysis ?? true,
      enableCorrelationAnalysis: config.enableCorrelationAnalysis ?? true,
      enableExternalFeeds: config.enableExternalFeeds ?? true,
      enableBehavioralAnalysis: config.enableBehavioralAnalysis ?? true,
      analysisInterval: config.analysisInterval ?? 60000, // 1 minute
      threatRetentionDays: config.threatRetentionDays ?? 90,
      confidenceThreshold: config.confidenceThreshold ?? 70,
      falsePositiveThreshold: config.falsePositiveThreshold ?? 30,
      externalFeeds: config.externalFeeds ?? [],
      behavioralBaselines: config.behavioralBaselines ?? []
    };

    this.stats = {
      totalThreats: 0,
      threatsByType: {},
      threatsBySeverity: {},
      threatsBySource: {},
      averageConfidence: 0,
      falsePositiveRate: 0,
      detectionRate: 0,
      responseTime: 0,
      activePatterns: 0,
      recentThreats: [],
      topAttackVectors: [],
      geographicDistribution: []
    };

    this.initializeService();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): SecurityThreatIntelligenceService {
    if (!SecurityThreatIntelligenceService.instance) {
      SecurityThreatIntelligenceService.instance = new SecurityThreatIntelligenceService();
    }
    return SecurityThreatIntelligenceService.instance;
  }

  /**
   * Initialize the service
   */
  private initializeService(): void {
    try {
      // Set up default threat patterns
      this.setupDefaultThreatPatterns();
      
      // Set up behavioral baselines
      this.setupBehavioralBaselines();
      
      // Start analysis if enabled
      if (this.config.enableRealTimeAnalysis) {
        this.startRealTimeAnalysis();
      }

      // Set up external feeds if enabled
      if (this.config.enableExternalFeeds) {
        this.setupExternalFeeds();
      }

      Logger.info('Security Threat Intelligence Service initialized', {
        config: this.config,
        patterns: this.patterns.size,
        baselines: this.config.behavioralBaselines.length
      });

    } catch (error) {
      Logger.error('Failed to initialize Security Threat Intelligence Service', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Set up default threat patterns
   */
  private setupDefaultThreatPatterns(): void {
    // SQL Injection Pattern
    const sqlInjectionPattern: ThreatPattern = {
      id: 'sql_injection',
      name: 'SQL Injection Attack',
      description: 'Detects SQL injection attempts',
      patternType: 'signature',
      indicators: [],
      rules: [
        {
          id: 'sql_keywords',
          name: 'SQL Keywords',
          condition: 'input',
          operator: 'regex',
          value: '/(union|select|insert|update|delete|drop|create|alter|exec|execute)/i',
          weight: 0.8,
          required: true
        },
        {
          id: 'sql_operators',
          name: 'SQL Operators',
          condition: 'input',
          operator: 'regex',
          value: '/(\'|\"|;|--|\/\*|\*\/)/',
          weight: 0.6,
          required: false
        }
      ],
      confidence: 0.85,
      severity: 'high',
      category: 'malware',
      created: new Date(),
      lastUpdated: new Date(),
      active: true,
      falsePositiveRate: 0.1,
      detectionCount: 0
    };

    // XSS Pattern
    const xssPattern: ThreatPattern = {
      id: 'xss_attack',
      name: 'Cross-Site Scripting Attack',
      description: 'Detects XSS attempts',
      patternType: 'signature',
      indicators: [],
      rules: [
        {
          id: 'script_tags',
          name: 'Script Tags',
          condition: 'input',
          operator: 'regex',
          value: '/(<script|<\/script|javascript:|on\w+\s*=)/i',
          weight: 0.9,
          required: true
        },
        {
          id: 'event_handlers',
          name: 'Event Handlers',
          condition: 'input',
          operator: 'regex',
          value: '/(onload|onclick|onmouseover|onerror)/i',
          weight: 0.7,
          required: false
        }
      ],
      confidence: 0.9,
      severity: 'high',
      category: 'malware',
      created: new Date(),
      lastUpdated: new Date(),
      active: true,
      falsePositiveRate: 0.05,
      detectionCount: 0
    };

    // Brute Force Pattern
    const bruteForcePattern: ThreatPattern = {
      id: 'brute_force',
      name: 'Brute Force Attack',
      description: 'Detects brute force login attempts',
      patternType: 'behavioral',
      indicators: [],
      rules: [
        {
          id: 'failed_attempts',
          name: 'Failed Login Attempts',
          condition: 'failed_logins',
          operator: 'range',
          value: { min: 5, max: 1000 },
          weight: 0.8,
          required: true
        },
        {
          id: 'time_window',
          name: 'Time Window',
          condition: 'time_window',
          operator: 'range',
          value: { min: 0, max: 15 }, // 15 minutes
          weight: 0.6,
          required: true
        }
      ],
      confidence: 0.75,
      severity: 'medium',
      category: 'malware',
      created: new Date(),
      lastUpdated: new Date(),
      active: true,
      falsePositiveRate: 0.15,
      detectionCount: 0
    };

    this.patterns.set(sqlInjectionPattern.id, sqlInjectionPattern);
    this.patterns.set(xssPattern.id, xssPattern);
    this.patterns.set(bruteForcePattern.id, bruteForcePattern);

    this.stats.activePatterns = this.patterns.size;
  }

  /**
   * Set up behavioral baselines
   */
  private setupBehavioralBaselines(): void {
    // Default baselines for common metrics
    const baselines: BehavioralBaseline[] = [
      {
        id: 'login_frequency',
        name: 'Login Frequency',
        system: 'authentication',
        metric: 'logins_per_hour',
        baseline: {
          mean: 2.5,
          stdDev: 1.2,
          min: 0,
          max: 10,
          percentile95: 5.8,
          percentile99: 8.2
        },
        windowSize: 30,
        lastUpdated: new Date()
      },
      {
        id: 'api_calls',
        name: 'API Call Frequency',
        system: 'api',
        metric: 'calls_per_minute',
        baseline: {
          mean: 15.3,
          stdDev: 8.7,
          min: 0,
          max: 50,
          percentile95: 35.2,
          percentile99: 45.8
        },
        windowSize: 7,
        lastUpdated: new Date()
      },
      {
        id: 'data_access',
        name: 'Data Access Patterns',
        system: 'database',
        metric: 'queries_per_hour',
        baseline: {
          mean: 45.2,
          stdDev: 22.1,
          min: 0,
          max: 150,
          percentile95: 95.3,
          percentile99: 125.7
        },
        windowSize: 14,
        lastUpdated: new Date()
      }
    ];

    this.config.behavioralBaselines = baselines;
  }

  /**
   * Start real-time analysis
   */
  private startRealTimeAnalysis(): void {
    this.analysisInterval = setInterval(() => {
      this.performRealTimeAnalysis();
    }, this.config.analysisInterval);

    Logger.info('Real-time threat analysis started');
  }

  /**
   * Set up external feeds
   */
  private setupExternalFeeds(): void {
    // Set up external threat intelligence feeds
    for (const feed of this.config.externalFeeds) {
      if (feed.enabled) {
        this.setupFeedUpdate(feed);
      }
    }

    Logger.info('External threat feeds configured', {
      feedCount: this.config.externalFeeds.length
    });
  }

  /**
   * Set up feed update
   */
  private setupFeedUpdate(feed: ExternalFeed): void {
    const updateFeed = async () => {
      try {
        await this.updateExternalFeed(feed);
      } catch (error) {
        Logger.error('External feed update failed', error instanceof Error ? error : new Error('Unknown error'));
      }
    };

    // Initial update
    updateFeed();

    // Set up periodic updates
    setInterval(updateFeed, feed.updateInterval * 60 * 1000);
  }

  /**
   * Update external feed
   */
  private async updateExternalFeed(feed: ExternalFeed): Promise<void> {
    try {
      // Implementation for updating external feeds
      Logger.info('External feed updated', { feedId: feed.id, feedName: feed.name });
      
      feed.lastUpdate = new Date();
    } catch (error) {
      Logger.error('Failed to update external feed', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Analyze threat indicators
   */
  public analyzeThreatIndicators(indicators: ThreatIndicator[]): ThreatAnalysis[] {
    const analyses: ThreatAnalysis[] = [];

    try {
      for (const indicator of indicators) {
        const analysis = this.analyzeIndicator(indicator);
        if (analysis) {
          analyses.push(analysis);
        }
      }

      // Store analyses
      for (const analysis of analyses) {
        this.analyses.set(analysis.threatId, analysis);
      }

      // Update statistics
      this.updateAnalysisStats(analyses);

      Logger.info('Threat indicators analyzed', {
        indicatorCount: indicators.length,
        analysisCount: analyses.length
      });

    } catch (error) {
      Logger.error('Failed to analyze threat indicators', error instanceof Error ? error : new Error('Unknown error'));
    }

    return analyses;
  }

  /**
   * Analyze single indicator
   */
  private analyzeIndicator(indicator: ThreatIndicator): ThreatAnalysis | null {
    try {
      const findings: ThreatFinding[] = [];
      let riskScore = 0;
      let confidence = 0;
      const relatedThreats: string[] = [];

      // Check against threat patterns
      for (const pattern of this.patterns.values()) {
        if (!pattern.active) continue;

        const patternMatch = this.checkPatternMatch(indicator, pattern);
        if (patternMatch) {
          findings.push({
            id: this.generateFindingId(),
            type: 'pattern_match',
            description: `Matches pattern: ${pattern.name}`,
            severity: pattern.severity,
            confidence: pattern.confidence,
            evidence: { patternId: pattern.id, patternName: pattern.name },
            source: 'pattern_matching',
            timestamp: new Date()
          });

          riskScore += pattern.confidence * 10;
          confidence = Math.max(confidence, pattern.confidence);
          relatedThreats.push(pattern.id);
        }
      }

      // Check behavioral anomalies
      if (this.config.enableBehavioralAnalysis) {
        const behavioralAnomaly = this.checkBehavioralAnomaly(indicator);
        if (behavioralAnomaly) {
          findings.push(behavioralAnomaly);
          riskScore += behavioralAnomaly.confidence * 5;
          confidence = Math.max(confidence, behavioralAnomaly.confidence);
        }
      }

      // Check correlation with other threats
      if (this.config.enableCorrelationAnalysis) {
        const correlationMatches = this.checkCorrelation(indicator);
        for (const match of correlationMatches) {
          findings.push(match);
          riskScore += match.confidence * 3;
          confidence = Math.max(confidence, match.confidence);
        }
      }

      if (findings.length === 0) {
        return null;
      }

      const analysis: ThreatAnalysis = {
        threatId: indicator.id,
        analysisType: 'real_time',
        timestamp: new Date(),
        findings,
        riskScore: Math.min(100, riskScore),
        recommendations: this.generateRecommendations(findings),
        confidence: Math.min(100, confidence * 100),
        falsePositiveProbability: this.calculateFalsePositiveProbability(findings),
        relatedThreats
      };

      return analysis;

    } catch (error) {
      Logger.error('Failed to analyze indicator', error instanceof Error ? error : new Error('Unknown error'));
      return null;
    }
  }

  /**
   * Check pattern match
   */
  private checkPatternMatch(indicator: ThreatIndicator, pattern: ThreatPattern): boolean {
    try {
      for (const rule of pattern.rules) {
        const matches = this.evaluateRule(indicator, rule);
        if (rule.required && !matches) {
          return false;
        }
      }
      return true;
    } catch (error) {
      Logger.error('Pattern matching failed', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }

  /**
   * Evaluate rule
   */
  private evaluateRule(indicator: ThreatIndicator, rule: ThreatRule): boolean {
    try {
      const value = this.extractValue(indicator, rule.condition);
      
      switch (rule.operator) {
        case 'equals':
          return value === rule.value;
        case 'contains':
          return typeof value === 'string' && value.includes(rule.value);
        case 'regex':
          return typeof value === 'string' && new RegExp(rule.value).test(value);
        case 'range':
          return typeof value === 'number' && value >= rule.value.min && value <= rule.value.max;
        case 'exists':
          return value !== undefined && value !== null;
        case 'not_exists':
          return value === undefined || value === null;
        default:
          return false;
      }
    } catch (error) {
      Logger.error('Rule evaluation failed', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }

  /**
   * Extract value from indicator
   */
  private extractValue(indicator: ThreatIndicator, condition: string): any {
    switch (condition) {
      case 'input':
        return indicator.value;
      case 'failed_logins':
        return indicator.context?.timePattern?.hour || 0;
      case 'time_window':
        return 0; // Placeholder
      default:
        return indicator.value;
    }
  }

  /**
   * Check behavioral anomaly
   */
  private checkBehavioralAnomaly(indicator: ThreatIndicator): ThreatFinding | null {
    try {
      // Find relevant baseline
      const baseline = this.findRelevantBaseline(indicator);
      if (!baseline) return null;

      // Calculate anomaly score
      const anomalyScore = this.calculateAnomalyScore(indicator, baseline);
      
      if (anomalyScore > 2.0) { // 2 standard deviations
        return {
          id: this.generateFindingId(),
          type: 'behavior_anomaly',
          description: `Behavioral anomaly detected in ${baseline.name}`,
          severity: anomalyScore > 3.0 ? 'high' : 'medium',
          confidence: Math.min(0.9, anomalyScore / 4.0),
          evidence: { baselineId: baseline.id, anomalyScore },
          source: 'behavioral_analysis',
          timestamp: new Date()
        };
      }

      return null;
    } catch (error) {
      Logger.error('Behavioral anomaly check failed', error instanceof Error ? error : new Error('Unknown error'));
      return null;
    }
  }

  /**
   * Find relevant baseline
   */
  private findRelevantBaseline(indicator: ThreatIndicator): BehavioralBaseline | null {
    // Simple matching logic - in practice, this would be more sophisticated
    for (const baseline of this.config.behavioralBaselines) {
      if (baseline.system === 'authentication' && indicator.type === 'behavior_pattern') {
        return baseline;
      }
    }
    return null;
  }

  /**
   * Calculate anomaly score
   */
  private calculateAnomalyScore(indicator: ThreatIndicator, baseline: BehavioralBaseline): number {
    // Simplified anomaly calculation
    const value = indicator.frequency || 0;
    const mean = baseline.baseline.mean;
    const stdDev = baseline.baseline.stdDev;
    
    return Math.abs(value - mean) / stdDev;
  }

  /**
   * Check correlation
   */
  private checkCorrelation(indicator: ThreatIndicator): ThreatFinding[] {
    const findings: ThreatFinding[] = [];

    try {
      // Check for correlation with other indicators
      for (const otherIndicator of this.indicators.values()) {
        if (otherIndicator.id === indicator.id) continue;

        const correlation = this.calculateCorrelation(indicator, otherIndicator);
        if (correlation > 0.7) {
          findings.push({
            id: this.generateFindingId(),
            type: 'correlation_match',
            description: `Correlation detected with indicator ${otherIndicator.id}`,
            severity: 'medium',
            confidence: correlation,
            evidence: { 
              correlatedIndicator: otherIndicator.id,
              correlationScore: correlation
            },
            source: 'correlation_analysis',
            timestamp: new Date()
          });
        }
      }
    } catch (error) {
      Logger.error('Correlation check failed', error instanceof Error ? error : new Error('Unknown error'));
    }

    return findings;
  }

  /**
   * Calculate correlation between indicators
   */
  private calculateCorrelation(indicator1: ThreatIndicator, indicator2: ThreatIndicator): number {
    // Simplified correlation calculation
    if (indicator1.type !== indicator2.type) return 0;
    
    const timeDiff = Math.abs(indicator1.lastSeen.getTime() - indicator2.lastSeen.getTime());
    const timeThreshold = 24 * 60 * 60 * 1000; // 24 hours
    
    if (timeDiff > timeThreshold) return 0;
    
    return 1 - (timeDiff / timeThreshold);
  }

  /**
   * Generate recommendations
   */
  private generateRecommendations(findings: ThreatFinding[]): string[] {
    const recommendations: string[] = [];

    for (const finding of findings) {
      switch (finding.type) {
        case 'pattern_match':
          recommendations.push('Block the source IP address');
          recommendations.push('Review and update security rules');
          break;
        case 'behavior_anomaly':
          recommendations.push('Investigate user behavior patterns');
          recommendations.push('Consider additional authentication');
          break;
        case 'correlation_match':
          recommendations.push('Analyze related security events');
          recommendations.push('Check for coordinated attacks');
          break;
      }
    }

    return [...new Set(recommendations)]; // Remove duplicates
  }

  /**
   * Calculate false positive probability
   */
  private calculateFalsePositiveProbability(findings: ThreatFinding[]): number {
    let totalProbability = 0;
    let count = 0;

    for (const finding of findings) {
      // Simplified false positive calculation
      const baseProbability = 0.1; // 10% base false positive rate
      const confidenceFactor = 1 - (finding.confidence / 100);
      const probability = baseProbability * confidenceFactor;
      
      totalProbability += probability;
      count++;
    }

    return count > 0 ? totalProbability / count : 0;
  }

  /**
   * Perform real-time analysis
   */
  private performRealTimeAnalysis(): void {
    try {
      // Analyze recent indicators
      const recentIndicators = Array.from(this.indicators.values())
        .filter(indicator => 
          Date.now() - indicator.lastSeen.getTime() < this.config.analysisInterval
        );

      if (recentIndicators.length > 0) {
        this.analyzeThreatIndicators(recentIndicators);
      }

    } catch (error) {
      Logger.error('Real-time analysis failed', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Update analysis statistics
   */
  private updateAnalysisStats(analyses: ThreatAnalysis[]): void {
    this.stats.totalThreats = this.analyses.size;
    
    // Update confidence statistics
    const confidences = analyses.map(a => a.confidence);
    this.stats.averageConfidence = confidences.length > 0 
      ? confidences.reduce((a, b) => a + b, 0) / confidences.length 
      : 0;

    // Update recent threats
    this.stats.recentThreats = Array.from(this.threats.values())
      .sort((a, b) => b.published.getTime() - a.published.getTime())
      .slice(0, 10);
  }

  /**
   * Generate finding ID
   */
  private generateFindingId(): string {
    return `FND-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  }

  /**
   * Get threat intelligence statistics
   */
  public getThreatStats(): ThreatIntelligenceStats {
    return { ...this.stats };
  }

  /**
   * Get all threat patterns
   */
  public getThreatPatterns(): ThreatPattern[] {
    return Array.from(this.patterns.values());
  }

  /**
   * Get all threat analyses
   */
  public getThreatAnalyses(): ThreatAnalysis[] {
    return Array.from(this.analyses.values());
  }

  /**
   * Add threat indicator
   */
  public addThreatIndicator(indicator: Omit<ThreatIndicator, 'id' | 'firstSeen' | 'lastSeen'>): ThreatIndicator {
    const newIndicator: ThreatIndicator = {
      ...indicator,
      id: this.generateIndicatorId(),
      firstSeen: new Date(),
      lastSeen: new Date()
    };

    this.indicators.set(newIndicator.id, newIndicator);
    return newIndicator;
  }

  /**
   * Generate indicator ID
   */
  private generateIndicatorId(): string {
    return `IND-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  }
}

// Export singleton instance
const securityThreatIntelligence = new SecurityThreatIntelligenceService();
export default securityThreatIntelligence;
